import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';

import { Card } from '@/components/ui/card';
import { CustomerAutocomplete } from '@/components/ui/customer-autocomplete';
import { Separator } from '@/components/ui/separator';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { useEntityForm } from '@/hooks/useEntityForm';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import {
	VehicleMakeAutocomplete,
	VehicleModelAutocomplete
} from '@/components/ui/vehicle-make-model-autocomplete';
import { VehicleColorAutocomplete } from '@/components/ui/vehicle-color-autocomplete';

import type { VehicleFormSubmitData } from './vehicle-input';

import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

interface VehicleTypeOption {
	value: string;
	label: string;
}

const createVehicleFormSchema = (t: (key: string) => string) =>
	z.object({
		plateNumber: z
			.string()
			.min(1, t('vehicles:validation.plateNumber.required'))
			.max(50, t('vehicles:validation.plateNumber.maxLength'))
			.transform(val => val.trim()),
		vehicleTypeId: z
			.string({
				required_error: t('vehicles:validation.vehicleType.required')
			})
			.min(1, t('vehicles:validation.vehicleType.required'))
			.regex(/^\d+$/, t('vehicles:validation.vehicleType.invalid'))
			.refine(val => {
				const num = Number(val);
				return Number.isInteger(num) && num > 0;
			}, t('vehicles:validation.vehicleType.positive')),
		customerId: z
			.string()
			.optional()
			.nullable()
			.or(z.literal('')),
		make: z
			.string()
			.max(255, t('vehicles:validation.make.maxLength'))
			.optional()
			.nullable()
			.or(z.literal('')),
		model: z
			.string()
			.max(255, t('vehicles:validation.model.maxLength'))
			.optional()
			.nullable()
			.or(z.literal('')),
		color: z
			.string()
			.max(100, t('vehicles:validation.color.maxLength'))
			.optional()
			.nullable()
			.or(z.literal('')),
		isActive: z.boolean()
	});

export type VehicleFormValues = z.infer<
	ReturnType<typeof createVehicleFormSchema>
>;

interface VehicleFormProps {
	mode: 'create' | 'edit' | 'clone';
	initialData?: Partial<VehicleFormSubmitData>;
	onSubmit: (data: VehicleFormSubmitData) => void;
	isSubmitting: boolean;
	vehicleTypeOptions: VehicleTypeOption[];
	isVehicleTypeLoading?: boolean;
	headerTitle: string;
	onBack?: () => void;
}

export const VehicleForm = ({
	mode,
	initialData,
	onSubmit,
	isSubmitting,
	vehicleTypeOptions,
	isVehicleTypeLoading = false,
	headerTitle,
	onBack
}: VehicleFormProps) => {
	const { t } = useTranslation();
	const vehicleFormSchema = useMemo(() => createVehicleFormSchema(t), [t]);

	const defaultVehicleTypeId = useMemo(() => {
		const carOption = vehicleTypeOptions.find(
			option => option.label.toLowerCase() === 'car'
		);
		return carOption?.value ?? '';
	}, [vehicleTypeOptions]);

	// Merge initialData into defaultValues for create mode (e.g., pre-filled plate number from autocomplete)
	const defaultValues = useMemo(() => ({
		plateNumber: initialData?.plateNumber || '',
		vehicleTypeId: initialData?.vehicleTypeId || '',
		customerId: initialData?.customerId,
		make: initialData?.make,
		model: initialData?.model,
		color: initialData?.color,
		isActive: initialData?.isActive ?? true
	}), [initialData]);

	const form = useEntityForm<VehicleFormValues>({
		mode,
		schema: vehicleFormSchema,
		defaultValues,
		initialData: initialData as VehicleFormValues | undefined
	});

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	const { control, handleSubmit } = form;
	const plateNumberInputRef = useRef<HTMLInputElement>(null);

	// Watch make value at component level to properly trigger re-renders
	const makeValue = form.watch('make');

	// Auto-focus plate number field when creating a new vehicle
	useEffect(() => {
		if (mode === 'create') {
			setTimeout(() => {
				plateNumberInputRef.current?.focus();
			}, 0);
		}
	}, [mode]);

	useEffect(() => {
		if (mode !== 'create' || !defaultVehicleTypeId) {
			return;
		}

		const currentVehicleTypeId = form.getValues('vehicleTypeId');
		if (currentVehicleTypeId) {
			return;
		}

		form.setValue('vehicleTypeId', defaultVehicleTypeId, {
			shouldDirty: false,
			shouldTouch: false,
			shouldValidate: true
		});
	}, [mode, defaultVehicleTypeId, form]);

	const handleFormSubmit = (values: VehicleFormValues) => {
		onSubmit({
			...values,
			vehicleId: initialData?.vehicleId
		});
	};

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				control={control}
				rightContent={
					<FormActionButtons
						mode={mode}
						isSubmitting={isSubmitting}
						validationErrors={form.formState.errors}
						formId="vehicle-form"
					/>
				}
			/>

			<Form {...form}>
			<form
				id="vehicle-form"
				onSubmit={handleSubmit(handleFormSubmit)}
				className="space-y-6"
			>
				<Card className="space-y-6">
					<div className="space-y-1">
						<h2 className="text-lg font-semibold">
							{t('vehicles:fields.vehicleDetails')}
						</h2>
					</div>					<div className="grid gap-6 md:grid-cols-2">
						<FormField
							control={control}
							name="plateNumber"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('vehicles:fields.plateNumber')} *
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											ref={plateNumberInputRef}
											value={field.value}
											placeholder={t(
												'vehicles:placeholders.plateNumber'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={control}
							name="vehicleTypeId"
							render={({ field }) => (
								<FormItem>
								<FormLabel>
										{t('vehicles:fields.vehicleType')}
									</FormLabel>
									<FormControl>
										<SentryDropdown
											options={vehicleTypeOptions}
											value={String(field.value ?? '')}
											onChange={value => {
												if (Array.isArray(value)) {
													return;
												}
												field.onChange(
													value === '' ? '' : value
												);
											}}
											placeholder={
												isVehicleTypeLoading
													? t(
															'vehicles:placeholders.vehicleTypeLoading'
														)
													: t(
															'vehicles:placeholders.vehicleType'
														)
											}
											disabled={
												isVehicleTypeLoading ||
												vehicleTypeOptions.length === 0
											}
											className="w-full"
											autoSelectFirstOption={false}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<Separator className="my-4" />

					<div className="space-y-1">
						<h3 className="text-base font-medium">
							{t('vehicles:fields.ownerSection')}
						</h3>
						<p className="text-sm text-muted-foreground">
							{t('vehicles:fields.ownerDescription')}
						</p>
					</div>

					<FormField
						control={control}
						name="customerId"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t('vehicles:fields.customer')}
								</FormLabel>
								<FormControl>
									<CustomerAutocomplete
										value={field.value ?? ''}
										onChange={(customerId) => {
											field.onChange(customerId || null);
										}}
										placeholder={t('vehicles:placeholders.customer')}
										initialSelectedLabel={initialData?.customerDisplayName}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<Separator className="my-4" />

					<div className="grid gap-6 md:grid-cols-3">
						<FormField
							control={control}
							name="make"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('vehicles:fields.make')}
									</FormLabel>
									<FormControl>
										<VehicleMakeAutocomplete
											value={field.value ?? ''}
											onChange={(value) => {
												field.onChange(value);
												// Reset model when make changes
												form.setValue('model', '');
											}}
											placeholder={t(
												'vehicles:placeholders.make'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={control}
							name="model"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('vehicles:fields.model')}
									</FormLabel>
									<FormControl>
										<VehicleModelAutocomplete
											value={field.value ?? ''}
											onChange={field.onChange}
											makeName={makeValue || null}
											placeholder={t(
												'vehicles:placeholders.model'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={control}
							name="color"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('vehicles:fields.color')}
									</FormLabel>
									<FormControl>
										<VehicleColorAutocomplete
											value={field.value ?? ''}
											onChange={field.onChange}
											placeholder={t(
												'vehicles:placeholders.color'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</Card>
			</form>
		</Form>
		</>
	);
};
